import { HttpClient } from '@adapters/HttpClient';
import { NoteModel, CreateNoteRequest, UpdateNoteRequest } from '../models/Note';

export interface NotesPage {
  notes: NoteModel[];
  nextPage?: number;
  hasMore: boolean;
  total: number;
}

/**
 * Adaptador para la API de notas CRM.
 * Documentación: GET /api/v1/crm/notes
 * Parámetros admitidos: order_id (opcional), page (default 1), per_page (default 25, max 100).
 * No se envían order_by ni sort; el orden lo define el backend.
 */
export class NotesApiAdapter {
  constructor(private httpClient: HttpClient) {}

  async getNotesPaginated(
    orderId?: number,
    page: number = 1,
    limit: number = 25
  ): Promise<NotesPage> {
    const params: Record<string, number> = {
      page,
      per_page: Math.min(Math.max(limit, 1), 100),
    };

    if (orderId !== undefined) {
      params.order_id = orderId;
    }

    const response = await this.httpClient.get<any>(
      '/crm/notes',
      params,
      {},
      page === 1
    );

    const notes = Array.isArray(response?.data) ? response.data : [];
    const meta = response?.meta ?? {
      current_page: 1,
      last_page: 1,
      total: 0,
    };

    const currentPage = meta.current_page ?? meta.currentPage ?? 1;
    const lastPage = meta.last_page ?? meta.lastPage ?? 1;
    const total = meta.total ?? 0;

    return {
      notes,
      nextPage: currentPage < lastPage ? currentPage + 1 : undefined,
      hasMore: currentPage < lastPage,
      total,
    };
  }

  async createNote(data: CreateNoteRequest): Promise<void> {
    await this.httpClient.post('/crm/notes', data);
  }

  async updateNote(noteId: number, data: UpdateNoteRequest): Promise<void> {
    await this.httpClient.put(`/crm/notes/${noteId}`, data);
  }

  async deleteNote(noteId: number): Promise<void> {
    await this.httpClient.delete(`/crm/notes/${noteId}`);
  }
}
